import React, { useEffect, useContext, useState } from 'react'
import { View, StyleSheet, ScrollView } from 'react-native'
import { Text, Button, Spinner, RangeDatepicker, Datepicker, Autocomplete, AutocompleteItem, Toggle, useTheme } from '@ui-kitten/components'
import { rootDb, fieldPath, getStatsForDays, functions, dualWriteLiftBlock } from '../../global/Firebase/firebase'
// timezoneUtils, not timezoneFunctions: the latter re-exports through modules that pull in
// expo-localization and global/Firebase, which sit outside jest's transformIgnorePatterns and
// break every suite that renders this screen. Same reason the ATL-1112 specs mock it.
import { dateRangeWithTimezoneAndAutoclose } from '../../utils/timezoneUtils'
import { overrideOrganizationSubscription } from '../../global/Firebase/organizationSubscription'
import { updateSupabaseSettingCache } from '../../global/Firebase/types'
import { organizationsApi, usersApi } from '../../global/API/typedApiClient'
import { UserContext } from '../../global/UserProvider'
import { format } from 'date-fns'
import HeaderIconButton from '../../components/HeaderIconButton'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import { useToast } from "react-native-toast-notifications"
import { useAtomValue, useAtom } from 'jotai'
import { storesDictAtom, organizationSubscriptionInfoAtom, organizationAtom, useSupabaseDeviceSettingAtom } from '../../global/AtomsProvider'
import { useTranslation } from 'react-i18next'
import { compareProductMovementsAndSourceDocs, compareProductReportAndDailyStats, generateProductReportForDate, validateDailyStats, validateDailyStatsId, validateProductStocksFromMovementsForRange } from '../../utils/validationFunctions'
import StoreChooserHorizontal from '../../components/StoreChooserHorizontal'
import { PRICING_PLANS } from '../../utils/pricingPlans'
import { queryDatabaseWriteLogs } from '../../utils/databaseWriteLogViewer'
import { exportToCSV } from '../../utils/exportUtils'

// Section Header Component
function SectionHeader({ title }) {
  const theme = useTheme()
  return (
    <View style={[styles.sectionHeader, { borderBottomColor: theme['border-basic-color-3'] }]}>
      <Text category='h6' style={[styles.sectionTitle, { color: theme['text-basic-color'] }]}>{title}</Text>
    </View>
  )
}

// Toggle Item Component for consistent spacing
function ToggleItem({ checked, onChange, label, description, testID, containerTestID }) {
  const theme = useTheme()
  return (
    <View style={styles.toggleItem} testID={containerTestID}>
      <Toggle
        checked={checked}
        onChange={onChange}
        testID={testID}
      >
        {label}
      </Toggle>
      {description && (
        <Text style={[styles.toggleDescription, { color: theme['text-hint-color'] }]}>{description}</Text>
      )}
    </View>
  )
}

// ============================================
// ORGANIZATION MANAGEMENT SECTION
// Subscription + Unlock combined
// ============================================

function OrganizationManagementSection({ toast }) {
  const theme = useTheme()
  const [context] = useContext(UserContext)
  const db = context.db
  const organizationSubscriptionInfo = useAtomValue(organizationSubscriptionInfoAtom)
  // ATL-851: the write below needs the organization OBJECT, not the derived subscription view's
  // `ref` — in Supabase read mode that ref is a plain `{ id }` shim with no `.update`.
  const organization = useAtomValue(organizationAtom)

  // Subscription state
  const [selectedPlan, setSelectedPlan] = useState(organizationSubscriptionInfo?.subscriptionPlan)
  const [selectedStatus, setSelectedStatus] = useState(organizationSubscriptionInfo?.subscriptionStatus)

  // Unlock state
  const [unlockDate, setUnlockDate] = useState(new Date())
  const [isLoading, setIsLoading] = useState(false)
  const datepicker = React.createRef()

  const updateSubscription = async () => {
    if (!selectedPlan || !selectedStatus) {
      toast.show('Выберите тариф и статус', {type: 'warning'})
      return
    }
    try {
      // ATL-855: this is the STAFF override, and it now goes through a server endpoint gated on
      // `users.is_super_admin` — deliberately not the per-org `admin` role, which every merchant
      // holds for their own organization. This screen is registered unconditionally in AppStack
      // and reachable at /misc, so the old direct Firestore write meant any authenticated user
      // could set their own org's paid plan. It writes both stores server-side.
      //
      // ⚠️ Nobody holds `is_super_admin` yet (0 of 944 users, 2026-08-05), so this returns 403
      // until Atlas staff accounts are provisioned — surfaced below rather than swallowed.
      await overrideOrganizationSubscription(organization, {
        plan: selectedPlan,
        status: selectedStatus,
      })
      toast.show('Подписка обновлена', {type: 'success'})
    } catch (error) {
      console.error('Error updating subscription:', error)
      toast.show('Ошибка при обновлении подписки: ' + (error?.message || ''), {type: 'error'})
    }
  }

  const unlockOrganization = async () => {
    setIsLoading(true)
    await db.update({liftBlockTill: unlockDate})
    // ATL-615: dual-write the lift override to Supabase (best-effort, non-fatal) so the gate stays
    // correct after the Phase-5 read cutover. db.id is the org's Firestore id (backend resolves it).
    if (db?.id) void dualWriteLiftBlock(db.id, unlockDate)
    setIsLoading(false)
    datepicker.current?.blur()
    toast.show('Организация разблокирована', {type: 'success'})
  }

  return (
    <View>
      {/* Subscription Management */}
      <Text style={[styles.subheading, { color: theme['text-basic-color'] }]}>Изменить подписку</Text>

      <PlatformDependantSelect
        data={PRICING_PLANS.map(plan => ({
          id: plan.revenuecatProductId || 'free',
          text: plan.revenuecatProductId || 'free'
        }))}
        selectedOption={selectedPlan ? { id: selectedPlan, text: selectedPlan } : null}
        onSelect={(option) => setSelectedPlan(option.id)}
        style={styles.input}
      />

      <PlatformDependantSelect
        data={[
          { id: 'active', text: 'Активен' },
          { id: 'past_due', text: 'Оплата просрочена' },
          { id: 'paused', text: 'Приостановлен' },
          { id: 'deleted', text: 'Отменен' }
        ]}
        selectedOption={selectedStatus ? { id: selectedStatus, text: selectedStatus } : null}
        onSelect={(option) => setSelectedStatus(option.id)}
        style={styles.input}
      />

      <Button onPress={updateSubscription} appearance='outline'>
        Обновить подписку
      </Button>

      <View style={[styles.divider, { backgroundColor: theme['border-basic-color-3'] }]} />

      {/* Organization Unlock */}
      <Text style={[styles.subheading, { color: theme['text-basic-color'] }]}>Разблокировать организацию</Text>
      <Text style={[styles.description, { color: theme['text-hint-color'] }]}>
        Временно разблокировать организацию до указанной даты
      </Text>

      <Datepicker
        placement='top'
        min={new Date('2020-12-01')}
        max={new Date('2029-12-31')}
        date={unlockDate}
        ref={datepicker}
        onSelect={setUnlockDate}
        style={styles.input}
      />

      {isLoading ? (
        <Spinner />
      ) : (
        <Button onPress={unlockOrganization} appearance='outline'>
          Разблокировать до {format(unlockDate, 'dd.MM.yyyy')}
        </Button>
      )}
    </View>
  )
}

// TestAccountSection removed - inlined in main component

// ============================================
// BACKEND SETTINGS SECTION
// Device-level Supabase/Firestore toggle
// ============================================

function BackendSettingsSection({ toast }) {
  const theme = useTheme()
  const [useSupabase, setUseSupabase] = useAtom(useSupabaseDeviceSettingAtom)
  const organization = useAtomValue(organizationAtom)

  // Per-org READ-cutover gate (ATL-462). Source of truth is the org record; mirror it locally for
  // the toggle. In the launch (Firestore-read) posture the org loads from the Firestore doc, so we
  // dual-write the flag (Firestore org doc + backend/Supabase) to keep both read sources in sync.
  const [orgUseSupabase, setOrgUseSupabase] = useState(organization?.useSupabaseForData === true)
  const [orgSaving, setOrgSaving] = useState(false)

  useEffect(() => {
    setOrgUseSupabase(organization?.useSupabaseForData === true)
  }, [organization])

  const handleToggle = (checked) => {
    setUseSupabase(checked)
    // Also update the synchronous cache used by shouldUseSupabase()
    updateSupabaseSettingCache(checked)
    toast.show(
      checked ? 'Supabase включен (требуется перезапуск)' : 'Firestore включен (требуется перезапуск)',
      { type: 'info', duration: 4000 }
    )
  }

  const handleOrgToggle = async (checked) => {
    const orgId = organization?.firestoreId || organization?.id
    if (!orgId) {
      toast.show('Организация не найдена', { type: 'error' })
      return
    }
    setOrgSaving(true)
    setOrgUseSupabase(checked) // optimistic
    try {
      // The backend setter writes BOTH copies (Supabase then Firestore when enabling, Firestore first
      // when disabling) and is the audited path.
      //
      // ENABLING goes through it ALONE (ATL-2052): it refuses an org ATL-1459 mirror intents would not
      // cover, BEFORE either flag moves. Writing the Firestore flag here first — what this did — put
      // the tills on Supabase reads even when the backend then refused, and broke the setter's own
      // "Supabase first when enabling" ordering.
      //
      // DISABLING (the kill switch) still writes the Firestore flag the tills read first, so a
      // rollback lands even if the backend is unreachable, then lets the setter finish both copies.
      if (!checked) {
        await rootDb.collection('organizations').doc(orgId).update({
          useSupabaseForData: false,
          useSupabaseForDataLastUpdated: new Date(),
        })
      }
      await organizationsApi.setSupabaseReads(orgId, checked)
      // ATL-888: devices running a build that carries the live-rollback watch restart themselves
      // within seconds, in BOTH directions. Older builds only pick the change up at their next
      // start. The copy says "новые версии" rather than promising the whole fleet, because
      // ATL-887 measured how little of it is on a current bundle at any moment — an operator who
      // believes every till has already switched will not chase the ones that have not.
      toast.show(
        checked
          ? 'Чтение из Supabase включено. Устройства на новых версиях перезапустятся автоматически; остальные — при следующем запуске. Убедитесь, что дрейф данных устранён.'
          : 'Чтение возвращено на Firestore. Устройства на новых версиях перезапустятся автоматически в течение нескольких секунд; остальные — при следующем запуске. Проверьте, что все кассы вернулись.',
        { type: checked ? 'warning' : 'success', duration: 8000 }
      )
    } catch (error) {
      console.error('Error setting org-level Supabase reads:', error)
      toast.show('Ошибка при переключении источника чтения', { type: 'error' })
      setOrgUseSupabase(!checked) // revert
    } finally {
      setOrgSaving(false)
    }
  }

  return (
    <View>
      <ToggleItem
        checked={useSupabase}
        onChange={handleToggle}
        label="Использовать Supabase"
        description="Переключение между Supabase и Firestore на этом устройстве. Требуется перезапуск приложения для применения изменений."
        testID="toggle-use-supabase"
        containerTestID="backend-settings-supabase-toggle"
      />
      <Text style={[styles.description, { color: theme['text-hint-color'] }]}>
        Текущий backend (устройство): {useSupabase ? 'Supabase' : 'Firestore'}
      </Text>

      <View style={styles.orgToggleSection}>
        <ToggleItem
          checked={orgUseSupabase}
          onChange={orgSaving ? () => {} : handleOrgToggle}
          label="Чтение из Supabase для организации (cutover)"
          description="Переключает ВСЮ организацию на чтение из Supabase. Это и есть cutover — включайте только когда все пользователи на новой версии приложения и дрейф данных устранён (ATL-469). Запись в оба бэкенда продолжается независимо. Устройства на новых версиях перезапустятся автоматически (в обе стороны); устройства на старых сборках переключатся только при следующем запуске."
          testID="toggle-org-use-supabase"
          containerTestID="backend-settings-org-supabase-toggle"
        />
        <Text style={[styles.description, { color: theme['text-hint-color'] }]}>
          Источник чтения организации: {orgUseSupabase ? 'Supabase' : 'Firestore'}
        </Text>
      </View>
    </View>
  )
}

// ============================================
// DATABASE LOGS SECTION
// ============================================

function DatabaseLogsSection({ navigation, toast }) {
  const theme = useTheme()
  const organization = useAtomValue(organizationAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const [range, setRange] = useState({})
  const [store, setStore] = useState(Object.keys(storesDict)[0])
  const [isLoading, setIsLoading] = useState(false)
  const [enableDatabaseWriteLogging, setEnableDatabaseWriteLogging] = useState(
    organization?.enableDatabaseWriteLogging === true
  )
  const rangePicker = React.createRef()

  useEffect(() => {
    if (organization) {
      setEnableDatabaseWriteLogging(organization.enableDatabaseWriteLogging === true)
    }
  }, [organization])

  const updateLoggingSetting = async (checked) => {
    try {
      setEnableDatabaseWriteLogging(checked)
      const orgId = organization?.firestoreId || organization?.id
      if (orgId) {
        await rootDb.collection('organizations').doc(orgId).update({
          enableDatabaseWriteLogging: checked,
          enableDatabaseWriteLoggingLastUpdated: new Date()
        })
        toast.show(
          checked ? 'Database write logging включено' : 'Database write logging выключено',
          {type: 'success'}
        )
      } else {
        toast.show('Организация не найдена', {type: 'error'})
        setEnableDatabaseWriteLogging(!checked)
      }
    } catch (error) {
      console.error('Error updating enableDatabaseWriteLogging:', error)
      toast.show('Ошибка при обновлении настройки', {type: 'error'})
      setEnableDatabaseWriteLogging(!checked)
    }
  }

  const exportLogs = async () => {
    if (!range.startDate || !range.endDate) {
      toast.show('Выберите диапазон дат', {type: 'warning'})
      return
    }
    try {
      setIsLoading(true)

      const endDate = new Date(range.endDate)
      endDate.setHours(23, 59, 59, 999)

      const filters = {
        startDate: range.startDate,
        endDate: endDate,
        limit: 1000
      }

      if (store && store !== 'all') {
        filters.store = store
      }

      const result = await queryDatabaseWriteLogs(filters)
      const logs = result.logs || []

      if (logs.length === 0) {
        toast.show('Логи не найдены', {type: 'info'})
        return
      }

      const exportData = logs.map(log => {
        const timestamp = log.timestamp?.toDate ? log.timestamp.toDate() : new Date(log.timestamp)
        const endTimestamp = log.endTimestamp?.toDate ? log.endTimestamp.toDate() : new Date(log.endTimestamp)

        const collectionsSummary = Object.entries(log.summary?.collections || {})
          .map(([collection, stats]) => `${collection}(${stats.total} ops, ${stats.uniqueDocuments} docs)`)
          .join('; ')

        return {
          'ID': log.id,
          'Функция': log.functionName,
          'Дата начала': format(timestamp, 'yyyy-MM-dd HH:mm:ss'),
          'Дата окончания': format(endTimestamp, 'yyyy-MM-dd HH:mm:ss'),
          'Длительность (мс)': log.duration || 0,
          'Магазин': log.context?.store || '',
          'Сотрудник': log.context?.employeeId || '',
          'Всего операций': log.summary?.totalOperations || 0,
          'Коллекции': collectionsSummary,
        }
      })

      const filename = `database-write-logs-${format(range.startDate, 'd-M-y')}-${format(range.endDate, 'd-M-y')}`
      await exportToCSV(exportData, filename)

      toast.show(`Экспортировано ${logs.length} записей`, {type: 'success'})
    } catch (error) {
      console.error('Error exporting logs:', error)
      toast.show('Ошибка при экспорте логов', {type: 'error'})
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <View>
      <ToggleItem
        checked={enableDatabaseWriteLogging}
        onChange={updateLoggingSetting}
        label="Сохранять логи записи в БД"
        description="Логи всех операций записи сохраняются в Firestore для удаленного просмотра."
      />

      <View style={[styles.divider, { backgroundColor: theme['border-basic-color-3'] }]} />

      <Button
        onPress={() => navigation.navigate('DatabaseWriteLogs', { store: store || 'all' })}
        appearance='outline'
        style={styles.marginBottom}
      >
        Просмотр подробных логов
      </Button>

      <Text style={[styles.subheading, { color: theme['text-basic-color'] }]}>Экспорт в CSV</Text>

      <RangeDatepicker
        min={new Date('2020-12-01')}
        max={new Date('2029-12-31')}
        range={range}
        ref={rangePicker}
        onSelect={setRange}
        style={styles.input}
      />

      <StoreChooserHorizontal
        store={store}
        style={styles.storeChooser}
        showAll={true}
        onChange={setStore}
      />

      {isLoading ? (
        <Spinner />
      ) : (
        <Button onPress={exportLogs} appearance='outline'>
          Экспортировать логи в CSV
        </Button>
      )}
    </View>
  )
}

// ============================================
// DATA VALIDATION SECTION
// Daily Stats + Product Reports + Stock
// ============================================

function DataValidationSection({ navigation, route, toast }) {
  const theme = useTheme()
  const [context] = useContext(UserContext)
  const db = context.db
  const [range, setRange] = useState({})
  const [date, setDate] = useState(new Date())
  const [store, setStore] = useState(context.user.store?.id)
  const [isLoading, setIsLoading] = useState(false)
  const [salesCheckLoading, setSalesCheckLoading] = useState(false)
  const rangePicker = React.createRef()
  const datepicker = React.createRef()

  const screenName = route?.name || navigation?.getState?.()?.routes?.[navigation?.getState?.()?.index]?.name || 'MiscScreen'

  const runSalesCountCheck = async () => {
    setSalesCheckLoading(true)
    try {
      const result = await functions.httpsCallable('dailySalesCountCheckManual')()
      console.log('Sales count check result:', result.data)

      if (result.data.status === 'success') {
        if (result.data.discrepancies.length > 0) {
          toast.show(`Найдено ${result.data.discrepancies.length} расхождений`, {type: 'warning'})
        } else {
          toast.show('Расхождений не найдено', {type: 'success'})
        }
        toast.show(`Проверено ${result.data.organizationsChecked} организаций`, {type: 'info'})
      } else {
        toast.show(`Ошибка: ${result.data.message}`, {type: 'error'})
      }
    } catch (error) {
      console.error('Error calling dailySalesCountCheckManual:', error)
      toast.show('Ошибка при вызове функции проверки', {type: 'error'})
    } finally {
      setSalesCheckLoading(false)
    }
  }

  const validateStats = (shouldUpdate) => {
    rangePicker.current?.blur()
    const startDate = new Date(range.startDate)
    const endDate = new Date(range.endDate)
    while (startDate <= endDate) {
      void validateDailyStats(new Date(startDate), store, shouldUpdate, {sales: true, spendings: true, workingHours: true}, null, true, false, screenName)
      startDate.setDate(startDate.getDate() + 1)
    }
  }

  const validateStatsId = async (shouldFix) => {
    rangePicker.current?.blur()
    if (!range.startDate || !range.endDate) {
      toast.show('Выберите период', { type: 'warning' })
      return
    }
    // ATL-1112: `getStatsForDays` decodes its bounds by inverting the range encoding, so it must
    // be given an ENCODED range. This screen holds the raw picker state, which every other caller
    // runs through this function first; without it the decode subtracts an autoclose shift that
    // was never applied and reads the edge in a frame that never framed it.
    const localRange = dateRangeWithTimezoneAndAutoclose(range, store ? [store] : null)
    const stats = await getStatsForDays(db, localRange, store, false)
    // ATL-1116: `validateDailyStatsId` asks whether a Firestore dailyStats DOCUMENT's id agrees
    // with the `date` stored inside it, and repairs the document when it does not. In Supabase
    // read mode there IS no document — `daily_stats` is a derived view, the response carries
    // `ref: {id: undefined}`, and the id is synthesised client-side from the row's business DATE
    // rather than from `getDailyStatID(store, date)`. Running the check over those rows reports a
    // mismatch for every row of a store whose autoclose cutoff falls after the hour a date-only
    // value renders at, and the "fix" cannot fix anything because there is no `ref.update`. Say
    // so once instead of printing a false failure per row; `getStatsForDays` suppresses the same
    // call for the same reason.
    const documents = stats.filter(stat => typeof stat?.ref?.update === 'function')
    if (documents.length !== stats.length) {
      const skipped = stats.length - documents.length
      console.warn(
        `validateStatsId: skipped ${skipped} of ${stats.length} daily stat(s) with no Firestore ` +
          `document to validate — in Supabase read mode the id is synthesised from the row's ` +
          `business date and there is nothing to repair.`
      )
      toast.show(`Пропущено ${skipped} из ${stats.length}: нет документа для проверки`, { type: 'warning' })
    }
    for (const stat of documents) {
      void validateDailyStatsId(stat, shouldFix)
    }
  }

  return (
    <View>
      {/* Edit Daily Stat */}
      <Text style={[styles.subheading, { color: theme['text-basic-color'] }]}>Редактирование Daily Stat</Text>
      <Datepicker
        placement='top'
        min={new Date('2020-12-01')}
        max={new Date()}
        date={date}
        ref={datepicker}
        onSelect={setDate}
        style={styles.input}
      />
      <StoreChooserHorizontal store={store} style={styles.storeChooser} showAll={false} onChange={setStore} />
      <Button
        onPress={() => navigation.navigate('EditDailyStats', { date, store })}
        appearance='outline'
        style={styles.marginBottom}
      >
        Открыть редактор
      </Button>

      <View style={[styles.divider, { backgroundColor: theme['border-basic-color-3'] }]} />

      {/* Recalculate Daily Stat */}
      <Text style={[styles.subheading, { color: theme['text-basic-color'] }]}>Пересчитать Daily Stat</Text>
      <RangeDatepicker
        min={new Date('2020-12-01')}
        max={new Date('2029-12-31')}
        range={range}
        ref={rangePicker}
        onSelect={setRange}
        style={styles.input}
      />
      <StoreChooserHorizontal store={store} style={styles.storeChooser} showAll={false} onChange={setStore} />

      {isLoading ? (
        <Spinner />
      ) : (
        <View style={styles.buttonRow}>
          <Button onPress={() => validateStats(false)} appearance='outline' style={styles.flexButton}>
            Проверить
          </Button>
          <Button onPress={() => validateStats(true)} appearance='outline' style={[styles.flexButton, styles.marginLeft]}>
            Обновить
          </Button>
        </View>
      )}

      <View style={[styles.divider, { backgroundColor: theme['border-basic-color-3'] }]} />

      {/* Fix Daily Stat ID */}
      <Text style={[styles.subheading, { color: theme['text-basic-color'] }]}>Исправить ID Daily Stat</Text>
      <View style={styles.buttonRow}>
        <Button onPress={() => validateStatsId(false)} appearance='outline' style={styles.flexButton}>
          Проверить
        </Button>
        <Button onPress={() => validateStatsId(true)} appearance='outline' style={[styles.flexButton, styles.marginLeft]}>
          Исправить
        </Button>
      </View>

      <View style={[styles.divider, { backgroundColor: theme['border-basic-color-3'] }]} />

      {/* Product Reports */}
      <Text style={[styles.subheading, { color: theme['text-basic-color'] }]}>Пересчитать Daily Product Report</Text>
      {isLoading ? (
        <Spinner />
      ) : (
        <Button onPress={async () => {
          rangePicker.current?.blur()
          const startDate = new Date(range.startDate)
          const endDate = new Date(range.endDate)
          const totalDays = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1
          console.log(`[ProductReport] Starting generation for ${totalDays} day(s): ${format(startDate, 'dd.MM.yyyy')} - ${format(endDate, 'dd.MM.yyyy')}, store: ${store}`)
          let day = 0
          while (startDate <= endDate) {
            day++
            const currentDate = new Date(startDate)
            console.log(`[ProductReport] Generating day ${day}/${totalDays}: ${format(currentDate, 'dd.MM.yyyy')}`)
            try {
              await generateProductReportForDate(currentDate, store)
              console.log(`[ProductReport] Done day ${day}/${totalDays}: ${format(currentDate, 'dd.MM.yyyy')}`)
            } catch (error) {
              console.error(`[ProductReport] Error day ${day}/${totalDays}: ${format(currentDate, 'dd.MM.yyyy')}`, error)
            }
            startDate.setDate(startDate.getDate() + 1)
          }
          console.log(`[ProductReport] Finished all ${totalDays} day(s)`)
        }} appearance='outline'>
          Пересчитать
        </Button>
      )}

      <View style={[styles.divider, { backgroundColor: theme['border-basic-color-3'] }]} />

      {/* Compare Reports */}
      <Text style={[styles.subheading, { color: theme['text-basic-color'] }]}>Сравнить Product Report и DailyStats</Text>
      <View style={styles.buttonRow}>
        <Button
          onPress={() => {
            compareProductReportAndDailyStats(range, store, false)
            rangePicker.current?.blur()
          }}
          appearance='outline'
          style={styles.flexButton}
        >
          Проверить
        </Button>
        <Button
          onPress={() => {
            compareProductReportAndDailyStats(range, store, true)
            rangePicker.current?.blur()
          }}
          appearance='outline'
          style={[styles.flexButton, styles.marginLeft]}
        >
          Обновить
        </Button>
      </View>

      <View style={[styles.divider, { backgroundColor: theme['border-basic-color-3'] }]} />

      {/* Stock Validation */}
      <Text style={[styles.subheading, { color: theme['text-basic-color'] }]}>Пересчитать остатки</Text>
      <View style={styles.buttonRow}>
        <Button
          onPress={() => {
            compareProductMovementsAndSourceDocs(range, store, false)
            datepicker.current?.blur()
          }}
          appearance='outline'
          size='small'
          style={styles.flexButton}
        >
          Compare movements
        </Button>
        <Button
          onPress={() => {
            validateProductStocksFromMovementsForRange(range, store)
            datepicker.current?.blur()
          }}
          appearance='outline'
          size='small'
          style={[styles.flexButton, styles.marginLeft]}
        >
          Check instock
        </Button>
      </View>

      <View style={[styles.divider, { backgroundColor: theme['border-basic-color-3'] }]} />

      {/* Sales Check */}
      <Text style={[styles.subheading, { color: theme['text-basic-color'] }]}>Проверка продаж</Text>
      <Text style={[styles.description, { color: theme['text-hint-color'] }]}>
        Проверка соответствия количества продаж в dailyStats и фактических документов
      </Text>

      {salesCheckLoading ? (
        <View style={styles.loadingContainer}>
          <Spinner />
          <Text style={[styles.loadingText, { color: theme['text-hint-color'] }]}>Проверка...</Text>
        </View>
      ) : (
        <Button onPress={runSalesCountCheck} appearance='outline'>
          Запустить проверку количества продаж
        </Button>
      )}
    </View>
  )
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function MiscScreen({ navigation, route }) {
  const { t } = useTranslation()
  const toast = useToast()
  const theme = useTheme()

  // Test Account state (inlined to fix Autocomplete issue)
  const [filteredOrgs, setFilteredOrgs] = useState([{id: 'T7RnwH5MoUt2HLCjSKAk', name: 'Тест 1', store: {id: 'EjMQir3KWc06kRCiXBaa', name: 'Талгар'} }])
  const [orgNameSearch, setOrgNameSearch] = useState('')
  const [orgIdSearch, setOrgIdSearch] = useState('')
  const [enforceMinQueryLength, setEnforceMinQueryLength] = useState(true)

  useEffect(() => {
    if (navigation.getState()?.index === 0) {
      navigation.setOptions({
        headerLeft: () => (
          <HeaderIconButton
            onPress={() => navigation.navigate('Home')}
            iconName='arrow-back-outline'
          />
        ),
      })
    }
  }, [navigation])

  const searchByName = (query) => {
    if (enforceMinQueryLength && query.length < 3) {
      setFilteredOrgs([])
      return
    }
    rootDb.collection('organizations')
      .orderBy('name')
      .startAt(query)
      .endAt(query + '\uf8ff')
      .limit(5)
      .get()
      .then((snap) => {
        const orgs = snap.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
          ref: doc.ref
        }))
        setFilteredOrgs(orgs)
      })
  }

  const searchById = (query) => {
    if (enforceMinQueryLength && query.length < 3) {
      setFilteredOrgs([])
      return
    }
    rootDb.collection('organizations')
      .orderBy(fieldPath.documentId())
      .startAt(query)
      .endAt(query + '\uf8ff')
      .limit(5)
      .get()
      .then((snap) => {
        const orgs = snap.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
          ref: doc.ref
        }))
        setFilteredOrgs(orgs)
      })
  }

  const selectOrganization = async (option) => {
    try {
      let store
      if (option.ref) {
        const snap = await option.ref.collection('stores').get()
        store = { ...snap.docs[0].data(), id: snap.docs[0].id, ref: snap.docs[0].ref }
      } else {
        store = option.store
      }
      await rootDb.collection('users').doc('bid6xWbiY7ObZDp4iBa9dhVgAXG3').update({
        organizationId: option.id,
        store,
        // Keep the root `storeId` field in lockstep with `store.id` — leaving it stale desynced
        // the Supabase store on a switch (ATL-545).
        storeId: store?.id,
        role: 'admin'
      })
      // Mirror the switch into Supabase: the chooser writes only Firestore, but FirebaseAuthGuard
      // resolves the org from the Supabase users row, so without this the backend keeps operating
      // on the PREVIOUS org after a switch (org-scoped reads/dual-writes 404/500). This makes
      // Supabase match the Firestore doc we just wrote (ATL-545). Best-effort: surfaced but
      // non-fatal — the Firestore switch itself already succeeded.
      try {
        await usersApi.syncSelfFromFirestore()
      } catch (syncError) {
        console.error('Error syncing org switch to Supabase:', syncError)
        toast.show('Firestore обновлён, но синхронизация Supabase не удалась', { type: 'warning', duration: 4000 })
      }
      setFilteredOrgs([])
      toast.show('Успешно', {duration: 3000})
    } catch (error) {
      console.error('Error updating user:', error)
      toast.show('Ошибка при обновлении пользователя', {type: 'error'})
    }
  }

  return (
    <ScrollView style={[styles.container, { backgroundColor: theme['background-basic-color-2'] }]} contentContainerStyle={styles.contentContainer} keyboardShouldPersistTaps='handled'>
      <Text category='h4' style={[styles.pageTitle, { color: theme['text-basic-color'] }]}>Административные настройки</Text>

      <View style={[styles.section, { backgroundColor: theme['background-basic-color-1'] }]}>
        <SectionHeader title="Настройки Backend" />
        <BackendSettingsSection toast={toast} />
      </View>

      <View style={[styles.section, { backgroundColor: theme['background-basic-color-1'] }]}>
        <SectionHeader title="Тестовый аккаунт" />
        <Autocomplete
          placeholder='Поиск по названию'
          value={orgNameSearch}
          onChangeText={(text) => {
            setOrgNameSearch(text)
            searchByName(text)
          }}
          onSelect={(index) => {
            void selectOrganization(filteredOrgs[index])
            setOrgNameSearch('')
          }}
          style={styles.input}
        >
          {filteredOrgs.map((item, index) => (
            <AutocompleteItem
              key={index}
              title={`${item.name} (${item.id}) (${item.lastActive?.toDate?.()?.toLocaleString?.('ru-RU') || 'N/A'})`}
            />
          ))}
        </Autocomplete>

        <Autocomplete
          placeholder='Поиск по ID'
          value={orgIdSearch}
          onChangeText={(text) => {
            setOrgIdSearch(text)
            searchById(text)
          }}
          onSelect={(index) => {
            void selectOrganization(filteredOrgs[index])
            setOrgIdSearch('')
          }}
          style={styles.input}
        >
          {filteredOrgs.map((item) => (
            <AutocompleteItem key={item.id} title={`${item.name} (${item.id})`} />
          ))}
        </Autocomplete>

        <ToggleItem
          checked={enforceMinQueryLength}
          onChange={setEnforceMinQueryLength}
          label="Минимум 3 символа для поиска"
        />
      </View>

      <View style={[styles.section, { backgroundColor: theme['background-basic-color-1'] }]}>
        <SectionHeader title="Управление организацией" />
        <OrganizationManagementSection toast={toast} />
      </View>

      <View style={[styles.section, { backgroundColor: theme['background-basic-color-1'] }]}>
        <SectionHeader title="Логи записи в БД" />
        <DatabaseLogsSection navigation={navigation} toast={toast} />
      </View>

      <View style={[styles.section, { backgroundColor: theme['background-basic-color-1'] }]}>
        <SectionHeader title="Валидация данных" />
        <DataValidationSection navigation={navigation} route={route} toast={toast} />
      </View>

      <View style={styles.bottomPadding} />
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  orgToggleSection: {
    marginTop: 16,
  },
  contentContainer: {
    padding: 16,
  },
  pageTitle: {
    marginBottom: 16,
  },
  section: {
    borderRadius: 12,
    marginBottom: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 2,
    overflow: 'visible',
    zIndex: 1,
  },
  sectionHeader: {
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
  },
  sectionTitle: {
  },
  description: {
    fontSize: 13,
    marginBottom: 16,
    lineHeight: 18,
  },
  input: {
    marginBottom: 12,
  },
  toggleItem: {
    marginBottom: 16,
    alignItems: 'flex-start',
  },
  toggleDescription: {
    fontSize: 12,
    marginTop: 8,
    marginLeft: 48,
    lineHeight: 16,
  },
  loadingContainer: {
    alignItems: 'center',
    paddingVertical: 24,
  },
  loadingText: {
    marginTop: 12,
  },
  buttonRow: {
    flexDirection: 'row',
  },
  flexButton: {
    flex: 1,
  },
  marginLeft: {
    marginLeft: 10,
  },
  marginBottom: {
    marginBottom: 12,
  },
  subheading: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 12,
    marginTop: 4,
  },
  storeChooser: {
    marginVertical: 10,
  },
  divider: {
    height: 1,
    marginVertical: 20,
  },
  bottomPadding: {
    height: 50,
  },
})
